'use client';

import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Search, RotateCcw, AlertCircle, Info } from 'lucide-react';
import DiplomaResultCard from './DiplomaResultCard';

const DIPLOMA_DATABASE = [
  {
    id: 'diploma-001',
    nome: 'Paulo Luiz Bernardino',
    cpf: '01581288697',
    cpfFormatado: '015.812.886-97',
    identidade: '16356087',
    numeroDiploma: '2024-ENG-CTR-047856',
    curso: 'Engenharia de Controle e Automação',
    habilitacao: 'Bacharelado',
    instituicao: 'Estácio de Sá',
    siglaInstituicao: 'ESTÁCIO',
    municipioInstituicao: 'Minas Gerais',
    dataColacao: '05/12/2024',
    dataRegistro: '08/01/2025',
    numeroRegistro: 'REG-2025-MG-047856',
    situacao: 'válido' as const,
    modalidade: 'Semi Presencial',
    cargaHoraria: '3.600 horas',
    anoIngresso: '2020',
  },
];

type FormValues = { query: string };
type SearchState = 'idle' | 'loading' | 'found' | 'not-found';

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function normalizeCPF(value: string): string {
  return value.replace(/\D/g, '');
}

export default function ConsultaSection() {
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [foundDiploma, setFoundDiploma] = useState<(typeof DIPLOMA_DATABASE)[0] | null>(null);
  const [inputValue, setInputValue] = useState('');

  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm<FormValues>({ mode: 'onSubmit' });

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const digitsOnly = raw.replace(/\D/g, '');
      const looksLikeCPF = digitsOnly.length > 0 && raw.replace(/[\d.\-\s]/g, '').length === 0;
      if (looksLikeCPF) {
        const formatted = formatCPF(raw);
        setInputValue(formatted);
        setValue('query', formatted);
      } else {
        setInputValue(raw.toUpperCase());
        setValue('query', raw.toUpperCase());
      }
    },
    [setValue]
  );

  const onSubmit = useCallback((data: FormValues) => {
    setSearchState('loading');
    setTimeout(() => {
      const query = data.query.trim();
      const normalizedQuery = normalizeCPF(query);
      const match = DIPLOMA_DATABASE.find((d) => {
        const cpfMatch = normalizeCPF(d.cpf) === normalizedQuery;
        const diplomaMatch = d.numeroDiploma.toUpperCase() === query.toUpperCase();
        const identidadeMatch = d.identidade !== undefined && d.identidade.replace(/\D/g, '') === normalizedQuery;
        return cpfMatch || diplomaMatch || identidadeMatch;
      });
      if (match) { setFoundDiploma(match); setSearchState('found'); }
      else { setFoundDiploma(null); setSearchState('not-found'); }
    }, 900);
  }, []);

  const handleNovaConsulta = useCallback(() => {
    setSearchState('idle');
    setFoundDiploma(null);
    setInputValue('');
    reset();
  }, [reset]);

  return (
    <section id="consulta-section" className="py-10 sm:py-14 px-4 sm:px-6 bg-background" aria-labelledby="consulta-title">
      <div className="max-w-screen-xl mx-auto">
        <div className="max-w-2xl mx-auto">

          {/* Section header */}
          <div className="mb-8">
            <h2
              id="consulta-title"
              className="text-2xl sm:text-3xl font-extrabold mb-3"
              style={{ color: '#1A1A2E', fontFamily: 'Montserrat, sans-serif' }}
            >
              Consulta de Diploma
            </h2>
            <div className="estacio-divider" aria-hidden="true" />
            <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Informe o CPF do formando (somente números ou no formato 000.000.000-00)
              ou o número do diploma para verificar a autenticidade do registro.
            </p>
          </div>

          {/* Search form */}
          {searchState !== 'found' && (
            <div className="bg-card border border-gray-200 rounded-xl shadow-card p-6 sm:p-8 mb-6">
              <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Formulário de consulta de diploma">
                <div className="mb-5">
                  <label htmlFor="query-input" className="block text-sm font-bold mb-1" style={{ color: '#1A1A2E', fontFamily: 'Montserrat, sans-serif' }}>
                    CPF do Formando ou Número do Diploma
                    <span className="text-error ml-1" aria-label="campo obrigatório">*</span>
                  </label>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Exemplos: <span className="font-mono font-medium">000.000.000-00</span> ou{' '}
                    <span className="font-mono font-medium">0000-ENG-CTR-000000</span>
                  </p>
                  <div className="relative">
                    <input
                      id="query-input"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Digite o CPF ou número do diploma"
                      value={inputValue}
                      aria-describedby={errors.query ? 'query-error' : 'query-hint'}
                      aria-invalid={!!errors.query}
                      className={`gov-input pr-12 ${errors.query ? 'error-state' : ''}`}
                      {...register('query', {
                        required: 'Informe o CPF ou o número do diploma.',
                        validate: (val) => val.trim().length < 5 ? 'O valor informado é muito curto.' : true,
                        onChange: handleInputChange,
                      })}
                    />
                    <Search size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
                  </div>
                  {errors.query && (
                    <p id="query-error" role="alert" className="flex items-center gap-1.5 mt-2 text-sm text-error font-medium">
                      <AlertCircle size={14} aria-hidden="true" />
                      {errors.query.message}
                    </p>
                  )}
                  <p id="query-hint" className="sr-only">Digite o CPF com ou sem formatação, ou o número completo do diploma.</p>
                </div>

                {searchState === 'not-found' && (
                  <div role="alert" className="flex items-start gap-3 rounded-lg p-4 mb-5" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                    <AlertCircle size={18} style={{ color: '#D97706' }} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#78350F', fontFamily: 'Montserrat, sans-serif' }}>Nenhum diploma encontrado</p>
                      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#92400E', fontFamily: 'Open Sans, sans-serif' }}>
                        Os dados informados não correspondem a nenhum registro em nossa base.
                        Verifique o CPF ou número do diploma e tente novamente.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    disabled={searchState === 'loading'}
                    className="gov-btn-primary flex-1 justify-center"
                    aria-label="Consultar diploma"
                  >
                    {searchState === 'loading' ? (
                      <>
                        <svg className="animate-spin-slow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                        Consultando...
                      </>
                    ) : (
                      <>
                        <Search size={18} aria-hidden="true" />
                        Consultar Diploma
                      </>
                    )}
                  </button>
                  {searchState === 'not-found' && (
                    <button type="button" onClick={handleNovaConsulta} className="gov-btn-secondary" aria-label="Limpar busca">
                      <RotateCcw size={16} aria-hidden="true" />
                      Limpar
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Result card */}
          {searchState === 'found' && foundDiploma && (
            <div className="result-card-enter">
              <DiplomaResultCard diploma={foundDiploma} onNovaConsulta={handleNovaConsulta} />
            </div>
          )}

          {/* Help info box */}
          {searchState === 'idle' && (
            <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <Info size={16} style={{ color: '#D97706' }} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-xs font-bold mb-1" style={{ color: '#D97706', fontFamily: 'Montserrat, sans-serif' }}>Como consultar?</p>
                <ul className="text-xs space-y-1 leading-relaxed list-none" style={{ color: '#78350F', fontFamily: 'Open Sans, sans-serif' }}>
                  <li>• Digite o CPF do formando com ou sem pontuação</li>
                  <li>• Ou informe o número completo do diploma (ex.: 0000-ENG-CTR-000000)</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}